/**
 * StrategyDashboard - Main view for the Strategy tab
 * Shows strategy selector, metrics, trade log, and AI assistance
 */

import React, { useState, useEffect, useRef } from 'react';
import { 
  Select, 
  Button, 
  Typography, 
  Tag, 
  Spin,
  Input,
  Skeleton,
  Empty,
  Segmented
} from 'antd';
import { 
  PlusOutlined,
  SendOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { useAppTheme } from '../../theme/ThemeProvider';
import { Strategy, Trade, AIAnalysisMessage, SplitMetrics } from './types';
import { getStrategies, analyzeStrategy } from '../../services/strategyService';

const { Text } = Typography;

interface StrategyDashboardProps {
  onBuildStrategy: () => void;
  onStrategySelect?: (strategy: Strategy | null) => void;
  onTimeSelect?: (timestamp: number) => void; // Jump to specific time on chart
}

const StrategyDashboard: React.FC<StrategyDashboardProps> = ({ 
  onBuildStrategy,
  onStrategySelect,
  onTimeSelect
}) => {
  const { t } = useTranslation();
  const { theme } = useAppTheme();

  // Strategy list and selection
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [selectedStrategy, setSelectedStrategy] = useState<Strategy | null>(null);
  const [isLoadingStrategies, setIsLoadingStrategies] = useState(true);

  // AI Analysis state
  const [chatHistory, setChatHistory] = useState<AIAnalysisMessage[]>([]);
  const [aiAnalysis, setAiAnalysis] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [followUpInput, setFollowUpInput] = useState('');
  const analysisContainerRef = useRef<HTMLDivElement>(null);
  const followUpInputRef = useRef<any>(null);
  const prevChatLengthRef = useRef(0);
  
  // Before/After split view mode
  const [splitViewMode, setSplitViewMode] = useState<'all' | 'before' | 'after'>('all');

  // Load strategies on mount
  useEffect(() => {
    loadStrategies();
  }, []);

  // Auto-scroll chat when new messages arrive
  useEffect(() => {
    const shouldScroll = chatHistory.length > prevChatLengthRef.current || isStreaming;
    if (shouldScroll && analysisContainerRef.current) {
      analysisContainerRef.current.scrollTop = analysisContainerRef.current.scrollHeight;
    }
    prevChatLengthRef.current = chatHistory.length;
  }, [chatHistory, aiAnalysis, isStreaming]);

  // Run AI analysis when strategy changes
  useEffect(() => {
    if (selectedStrategy?.backtest_results) {
      setChatHistory([]);
      setAiAnalysis('');
      runAIAnalysis();
    }
  }, [selectedStrategy]);

  const loadStrategies = async () => {
    try {
      setIsLoadingStrategies(true);
      const data = await getStrategies();
      setStrategies(data);
    } catch (error) {
      console.error('Failed to load strategies:', error);
    } finally {
      setIsLoadingStrategies(false);
    }
  };

  const handleStrategyChange = (strategyId: string) => {
    const strategy = strategies.find(s => s.strategy_id === strategyId) || null;
    setSelectedStrategy(strategy);
    onStrategySelect?.(strategy);
    setSplitViewMode('all'); // Reset split view mode when strategy changes
  };

  const runAIAnalysis = async (userMessage?: string) => {
    if (!selectedStrategy?.backtest_results) return;
    
    setIsAnalyzing(true);
    setIsStreaming(true);
    setAiAnalysis('');
    
    if (userMessage) {
      setChatHistory(prev => [...prev, { role: 'user', content: userMessage }]);
    }
    
    try {
      const request = {
        backtest_results: selectedStrategy.backtest_results,
        strategy_config: {
          symbol: selectedStrategy.symbol,
          timeframe: selectedStrategy.timeframe,
          sell_rule: selectedStrategy.sell_rule
        },
        buy_signal_name: selectedStrategy.name,
        message: userMessage,
        history: chatHistory,
        language: (i18n.language === 'zh-CN' ? 'zh-CN' : 'en-US') as 'zh-CN' | 'en-US'
      };
      
      let fullResponse = '';
      const generator = await analyzeStrategy(request);
      
      for await (const chunk of generator) {
        fullResponse += chunk;
        setAiAnalysis(fullResponse);
      }
      
      setChatHistory(prev => [...prev, { role: 'assistant', content: fullResponse }]);
      setAiAnalysis('');
    } catch (error) {
      console.error('AI analysis error:', error);
    } finally {
      setIsAnalyzing(false);
      setIsStreaming(false);
    }
  };

  const handleFollowUp = () => {
    if (!followUpInput.trim() || isAnalyzing) return;
    const message = followUpInput.trim();
    setFollowUpInput('');
    runAIAnalysis(message);
  };

  // Format action entry for Strategy Log (separate buy and sell)
  const formatActionEntry = (
    type: 'buy' | 'sell', 
    price: number, 
    time: number, 
    returnPct: number | null,
    index: number
  ) => {
    const isBuy = type === 'buy';
    const actionDate = new Date(time);
    const formattedDate = formatShortDate(actionDate);
    
    const handleClick = () => {
      // Convert ms to seconds for chart API
      const timeInSeconds = Math.floor(time / 1000);
      onTimeSelect?.(timeInSeconds);
    };
    
    return (
      <div 
        key={`${type}-${index}`}
        onClick={handleClick}
        style={{ 
          display: 'flex', 
          alignItems: 'center',
          padding: '10px 0',
          borderBottom: `1px solid ${theme.surfaceBorder}`,
          cursor: 'pointer',
          transition: 'background 0.2s',
          marginLeft: -8,
          marginRight: -8,
          paddingLeft: 8,
          paddingRight: 8,
          borderRadius: 8
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = theme.surfaceBackground;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
        }}
      >
        {/* Icon */}
        <div style={{
          width: 28,
          height: 28,
          borderRadius: 6,
          background: isBuy ? 'rgba(82, 196, 26, 0.15)' : 'rgba(255, 77, 79, 0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: 8,
          flexShrink: 0
        }}>
          <span style={{ 
            fontSize: 14, 
            color: isBuy ? theme.candleUp : theme.candleDown
          }}>
            {isBuy ? '↗' : '↘'}
          </span>
        </div>
        
        {/* Info */}
        <div style={{ flex: 1 }}>
          <div style={{ 
            fontWeight: 600, 
            fontSize: 12,
            color: isBuy ? theme.candleUp : theme.candleDown
          }}>
            {isBuy ? 'BUY' : 'SELL'} • {selectedStrategy?.symbol}
          </div>
          <div style={{ fontSize: 10, color: theme.textSecondary }}>
            ${price?.toLocaleString()} • {formattedDate}
          </div>
        </div>
        
        {/* Return (only show for sell) */}
        {!isBuy && returnPct !== null && (
          <div style={{ 
            fontWeight: 600, 
            fontSize: 12,
            color: returnPct >= 0 ? theme.candleUp : theme.candleDown
          }}>
            {returnPct >= 0 ? '+' : ''}{returnPct.toFixed(1)}%
          </div>
        )}
      </div>
    );
  };

  // Format date as "12/16 14:30"
  const formatShortDate = (date: Date): string => {
    const month = date.getUTCMonth() + 1;
    const day = date.getUTCDate();
    const hours = date.getUTCHours().toString().padStart(2, '0');
    const minutes = date.getUTCMinutes().toString().padStart(2, '0');
    return `${month}/${day} ${hours}:${minutes}`;
  };

  // Generate all actions (buy and sell separately) from trades
  const getAllActions = () => {
    if (!selectedStrategy?.backtest_results?.trades) return [];
    
    const signalTime = selectedStrategy.backtest_results.validation_split?.signal_time;
    
    // Filter trades based on split view mode
    let filteredTrades = selectedStrategy.backtest_results.trades;
    if (splitViewMode === 'before' && signalTime) {
      filteredTrades = selectedStrategy.backtest_results.trades.filter(
        trade => trade.entry_time < signalTime
      );
    } else if (splitViewMode === 'after' && signalTime) {
      filteredTrades = selectedStrategy.backtest_results.trades.filter(
        trade => trade.entry_time >= signalTime
      );
    }
    
    const actions: Array<{
      type: 'buy' | 'sell';
      price: number;
      time: number;
      returnPct: number | null;
      tradeIndex: number;
    }> = [];
    
    filteredTrades.forEach((trade, i) => {
      // Add buy action
      actions.push({
        type: 'buy',
        price: trade.entry_price,
        time: trade.entry_time,
        returnPct: null,
        tradeIndex: i
      });
      // Add sell action
      actions.push({
        type: 'sell',
        price: trade.exit_price,
        time: trade.exit_time,
        returnPct: trade.return_pct,
        tradeIndex: i
      });
    });
    
    // Sort by time descending (most recent first)
    return actions.sort((a, b) => b.time - a.time);
  };

  return (
    <div style={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      background: theme.surfaceBackground
    }}>
      {/* Scrollable Content */}
      <div style={{ 
        flex: 1, 
        overflowY: 'auto', 
        padding: '12px'
      }}>
        {/* Strategy Selector Card */}
        <div style={{
          background: theme.toolbarBackground,
          borderRadius: 10,
          border: `1px solid ${theme.surfaceBorder}`,
          padding: '12px',
          marginBottom: 12
        }}>
          {/* Strategy Dropdown */}
          <Select
            placeholder={t('strategyBuilder.selectStrategy', 'Select Strategy')}
            style={{ width: '100%', marginBottom: 10, fontSize: 12 }}
            value={selectedStrategy?.strategy_id}
            onChange={handleStrategyChange}
            loading={isLoadingStrategies}
            allowClear
            onClear={() => {
              setSelectedStrategy(null);
              onStrategySelect?.(null);
              setSplitViewMode('all');
            }}
          >
            {strategies.map(s => (
              <Select.Option key={s.strategy_id} value={s.strategy_id}>
                {s.name}
              </Select.Option>
            ))}
          </Select>

          {/* Metrics - Only show when strategy selected (6 metrics in 2 rows) */}
          {selectedStrategy?.backtest_results && (() => {
            const hasValidationSplit = !!selectedStrategy.backtest_results.validation_split;
            const validationSplit = selectedStrategy.backtest_results.validation_split;
            
            // Get metrics based on split view mode
            const getMetrics = () => {
              if (splitViewMode === 'all' || !hasValidationSplit) {
                return {
                  win_rate: selectedStrategy.backtest_results.win_rate ?? 0,
                  sharpe_ratio: selectedStrategy.backtest_results.sharpe_ratio ?? 0,
                  max_drawdown: selectedStrategy.backtest_results.max_drawdown ?? 0,
                  total_return: selectedStrategy.backtest_results.total_return ?? 0,
                  profit_factor: selectedStrategy.backtest_results.profit_factor ?? 0,
                  total_trades: selectedStrategy.backtest_results.total_trades ?? 0
                };
              } else if (splitViewMode === 'before' && validationSplit?.before) {
                return {
                  win_rate: validationSplit.before.win_rate ?? 0,
                  sharpe_ratio: validationSplit.before.sharpe_ratio ?? 0,
                  max_drawdown: validationSplit.before.max_drawdown ?? 0,
                  total_return: validationSplit.before.total_return ?? 0,
                  profit_factor: validationSplit.before.profit_factor ?? 0,
                  total_trades: validationSplit.before.total_trades ?? 0
                };
              } else if (splitViewMode === 'after' && validationSplit?.after) {
                return {
                  win_rate: validationSplit.after.win_rate ?? 0,
                  sharpe_ratio: validationSplit.after.sharpe_ratio ?? 0,
                  max_drawdown: validationSplit.after.max_drawdown ?? 0,
                  total_return: validationSplit.after.total_return ?? 0,
                  profit_factor: validationSplit.after.profit_factor ?? 0,
                  total_trades: validationSplit.after.total_trades ?? 0
                };
              }
              return {
                win_rate: 0,
                sharpe_ratio: 0,
                max_drawdown: 0,
                total_return: 0,
                profit_factor: 0,
                total_trades: 0
              };
            };
            
            const metrics = getMetrics();
            
            return (
            <div style={{ marginBottom: 10 }}>
              {/* Before/After Segmented Control - Only show when validation_split exists */}
              {hasValidationSplit && (
                <div style={{ marginBottom: 8 }}>
                  <Segmented
                    value={splitViewMode}
                    onChange={(value) => setSplitViewMode(value as 'all' | 'before' | 'after')}
                    options={[
                      { label: t('analysis.split.all', '全部'), value: 'all' },
                      { label: t('analysis.split.discovery', '之前'), value: 'before' },
                      { label: t('analysis.split.validation', '之后'), value: 'after' }
                    ]}
                    block
                    size="small"
                    style={{ fontSize: 11 }}
                  />
                </div>
              )}
              
              {/* Row 1 */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: 6,
                marginBottom: 6
              }}>
                <div>
                  <Text style={{ color: theme.textSecondary, fontSize: 9, display: 'block' }}>
                    WIN RATE
                  </Text>
                  <Text style={{ 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: theme.candleUp
                  }}>
                    {metrics.win_rate.toFixed(0)}%
                  </Text>
                </div>
                <div>
                  <Text style={{ color: theme.textSecondary, fontSize: 9, display: 'block' }}>
                    SHARPE
                  </Text>
                  <Text style={{ fontSize: 14, fontWeight: 600, color: theme.textPrimary }}>
                    {metrics.sharpe_ratio >= 99 ? '∞' : 
                     metrics.sharpe_ratio <= -99 ? '-∞' : 
                     (metrics.sharpe_ratio === 0 && metrics.win_rate === 100 && metrics.total_trades > 0) ? '∞' :
                     metrics.sharpe_ratio.toFixed(1)}
                  </Text>
                </div>
                <div>
                  <Text style={{ color: theme.textSecondary, fontSize: 9, display: 'block' }}>
                    MAX DRAW.
                  </Text>
                  <Text style={{ 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: theme.candleDown
                  }}>
                    -{Math.abs(metrics.max_drawdown).toFixed(1)}%
                  </Text>
                </div>
              </div>
              {/* Row 2 */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: 6
              }}>
                <div>
                  <Text style={{ color: theme.textSecondary, fontSize: 9, display: 'block' }}>
                    TOTAL RETURN
                  </Text>
                  <Text style={{ 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: metrics.total_return >= 0 ? theme.candleUp : theme.candleDown
                  }}>
                    {metrics.total_return >= 0 ? '+' : ''}
                    {metrics.total_return.toFixed(1)}%
                  </Text>
                </div>
                <div>
                  <Text style={{ color: theme.textSecondary, fontSize: 9, display: 'block' }}>
                    PROFIT FACTOR
                  </Text>
                  <Text style={{ fontSize: 14, fontWeight: 600, color: theme.textPrimary }}>
                    {metrics.profit_factor >= 99 ? '∞' : metrics.profit_factor.toFixed(2)}
                  </Text>
                </div>
                <div>
                  <Text style={{ color: theme.textSecondary, fontSize: 9, display: 'block' }}>
                    TRADES
                  </Text>
                  <Text style={{ fontSize: 14, fontWeight: 600, color: theme.textPrimary }}>
                    {metrics.total_trades}
                  </Text>
                </div>
              </div>
              
              {/* Futures Trading Info - Only show for futures mode */}
              {selectedStrategy.backtest_results.trading_mode === 'futures' && (
                <div style={{ 
                  marginTop: 8,
                  padding: 8, 
                  background: theme.surfaceBackground, 
                  borderRadius: 6,
                  fontSize: 10
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ 
                      background: selectedStrategy.backtest_results.position_direction === 'long' ? theme.candleUp : theme.candleDown,
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: 4,
                      fontWeight: 600
                    }}>
                      {selectedStrategy.backtest_results.position_direction === 'long' ? '🟢 Long' : '🔴 Short'}
                    </span>
                    <span style={{ color: theme.textSecondary }}>
                      {selectedStrategy.backtest_results.leverage_used || 1}x {t('strategyBuilder.leverage', '杠杆')}
                    </span>
                    {(selectedStrategy.backtest_results.liquidations ?? 0) > 0 && (
                      <span style={{ color: theme.candleDown }}>
                        💥 {selectedStrategy.backtest_results.liquidations} {t('strategyBuilder.liquidations', '爆仓')}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
            );
          })()}

          {/* Build Strategy Button */}
          <Button
            type="dashed"
            block
            icon={<PlusOutlined />}
            onClick={onBuildStrategy}
            style={{
              borderColor: theme.surfaceBorder,
              color: theme.textSecondary,
              height: 34,
              borderRadius: 6,
              fontSize: 12
            }}
          >
            {t('strategyBuilder.buildStrategy', 'Build Strategy')}
          </Button>
        </div>

        {/* Strategy Log - Only show when strategy selected */}
        {selectedStrategy?.backtest_results?.trades && selectedStrategy.backtest_results.trades.length > 0 && (
          <div style={{
            background: theme.toolbarBackground,
            borderRadius: 10,
            border: `1px solid ${theme.surfaceBorder}`,
            padding: '12px',
            marginBottom: 12
          }}>
            <Text style={{ 
              color: theme.textPrimary, 
              fontSize: 11, 
              fontWeight: 600,
              letterSpacing: 0.5,
              display: 'block',
              marginBottom: 10
            }}>
              STRATEGY LOG
            </Text>
            
            <div style={{ maxHeight: 280, overflowY: 'auto' }}>
              {getAllActions().map((action, i) => 
                formatActionEntry(action.type, action.price, action.time, action.returnPct, i)
              )}
            </div>
          </div>
        )}

        {/* Strategy Assistance - Only show when strategy selected */}
        {selectedStrategy && (
          <div style={{
            background: theme.toolbarBackground,
            borderRadius: 10,
            border: `1px solid ${theme.surfaceBorder}`,
            padding: '12px'
          }}>
            <Text style={{ 
              color: theme.textPrimary, 
              fontSize: 11, 
              fontWeight: 600,
              letterSpacing: 0.5,
              display: 'block',
              marginBottom: 10
            }}>
              STRATEGY ASSISTANCE
            </Text>
            
            {/* Chat Messages */}
            <div 
              ref={analysisContainerRef}
              style={{ 
                minHeight: 150,
                maxHeight: 300,
                overflowY: 'auto',
                marginBottom: 12
              }}
            >
              {/* Chat History */}
              {chatHistory.map((msg, index) => (
                <div 
                  key={index}
                  style={{
                    display: 'flex',
                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    marginBottom: 12
                  }}
                >
                  <div style={{
                    maxWidth: '85%',
                    padding: '8px 12px',
                    borderRadius: msg.role === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                    background: msg.role === 'user' ? '#1890ff' : theme.surfaceBackground,
                    color: msg.role === 'user' ? '#fff' : theme.textPrimary,
                    fontSize: 12,
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap'
                  }}>
                    {msg.content}
                  </div>
                </div>
              ))}
              
              {/* Streaming response */}
              {aiAnalysis && (
                <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 12 }}>
                  <div style={{
                    maxWidth: '85%',
                    padding: '8px 12px',
                    borderRadius: '12px 12px 12px 4px',
                    background: theme.surfaceBackground,
                    color: theme.textPrimary,
                    fontSize: 12,
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap'
                  }}>
                    {aiAnalysis}
                    {isStreaming && (
                      <span style={{ 
                        display: 'inline-block', 
                        width: 6, 
                        height: 14, 
                        background: '#1890ff', 
                        marginLeft: 2,
                        animation: 'blink 1s infinite',
                        verticalAlign: 'middle'
                      }} />
                    )}
                  </div>
                </div>
              )}
              
              {/* Loading state */}
              {isAnalyzing && !aiAnalysis && chatHistory.length === 0 && (
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <div style={{
                    maxWidth: '85%',
                    padding: '8px 12px',
                    borderRadius: '12px 12px 12px 4px',
                    background: theme.surfaceBackground
                  }}>
                    <Skeleton.Input active size="small" style={{ width: 200, marginBottom: 4 }} />
                    <Skeleton.Input active size="small" style={{ width: 150, marginBottom: 4 }} />
                    <Skeleton.Input active size="small" style={{ width: 180 }} />
                  </div>
                </div>
              )}
              
              {/* Empty state */}
              {!isAnalyzing && chatHistory.length === 0 && !aiAnalysis && (
                <Empty 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <Text style={{ color: theme.textSecondary, fontSize: 12 }}>
                      {t('strategyBuilder.waitingForAnalysis', 'Waiting for AI analysis...')}
                    </Text>
                  }
                />
              )}
            </div>
            
            {/* Input Area */}
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 12px',
              background: theme.surfaceBackground,
              borderRadius: 24
            }}>
              <Input
                ref={followUpInputRef}
                placeholder={t('strategyBuilder.askAboutStrategy', 'Ask AI about strategy performance...')}
                bordered={false}
                value={followUpInput}
                onChange={(e) => setFollowUpInput(e.target.value)}
                onPressEnter={handleFollowUp}
                disabled={isAnalyzing}
                style={{ flex: 1, background: 'transparent', fontSize: 12, color: theme.textPrimary }}
              />
              <Button
                type="text"
                icon={isAnalyzing ? <LoadingOutlined /> : <SendOutlined />}
                onClick={handleFollowUp}
                disabled={!followUpInput.trim() || isAnalyzing}
                style={{ 
                  color: followUpInput.trim() && !isAnalyzing ? '#1890ff' : theme.textSecondary,
                  padding: 4
                }}
              />
            </div>
          </div>
        )}

        {/* Empty state when no strategy selected */}
        {!selectedStrategy && !isLoadingStrategies && (
          <div style={{ 
            textAlign: 'center', 
            padding: '32px 16px'
          }}>
            <Text style={{ color: theme.textSecondary, fontSize: 12 }}>
              {t('strategyBuilder.selectOrBuildStrategy', 'Select a strategy or build a new one')}
            </Text>
          </div>
        )}
      </div>

      {/* CSS for blinking cursor */}
      <style>{`
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default StrategyDashboard;

